import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import { connect } from 'react-redux'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import config from 'constants/config'
import { fillArray } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'
import { useResponsive } from 'hooks/useResponsive'
import { setAppBannerCookie } from 'cookies/appBannerCookie'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LineBillIcon from 'LuxKit/Icons/line/LineBillIcon'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineUserCheckIcon from 'LuxKit/Icons/line/LineUserCheckIcon'

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`

const BannerContainer = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const BannerContent = styled(Group)`
  padding: ${rem(32)} ${rem(20)};
`

const Content = styled(Group)`
  padding: 0 ${rem(20)};
`

const CopyContainer = styled(Group)`
  min-height: ${rem(48)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

interface Props {
  currentRegionCode: string
  onClose?: () => void
  hasCpcUtmMedium: Boolean
  hasGoogleClickIdentifier: Boolean
}

let circuitBreak = 0
function ContinueToAppDrawer({ onClose = noop, hasCpcUtmMedium, hasGoogleClickIdentifier, currentRegionCode }: Props) {
  const intl = useIntl()
  const [url, setUrl] = useState<string>('')
  const [open, setOpen] = useState<boolean>(false)
  const [copied, setCopied] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setOpen(true)
        return window.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const [match] = useResponsive() // This useResponsive is ok to use because result will be used inside an useEffect and never in the SSR

  const uptoTablet = useMemo(() => match.mobile || match.tablet, [match])

  const promoAppBannerFeatureFlag = !!useOptimizelyExperiment(OptimizelyFeatureFlags.promoAppBanner)

  const regionPromotionData = config.appBanner?.promotionData[currentRegionCode] || {}
  const showSpecialCodeForAdClicks = !!regionPromotionData.specialCodeForAdClicks && (hasCpcUtmMedium || hasGoogleClickIdentifier)
  const code = showSpecialCodeForAdClicks ? regionPromotionData.specialCodeForAdClicks : regionPromotionData.code
  const { discount, minSpend } = regionPromotionData

  const promoAppDrawer = useMemo(() => {
    return (discount && code && minSpend) && promoAppBannerFeatureFlag
  }, [discount, code, minSpend, promoAppBannerFeatureFlag])

  useEffect(() => {
    // This is here because the AF_SMART_SCRIPT does not have a callback
    // So we need to try multiple times (5) to check if the script has loaded
    // Before we show the app banner
    const interval = setInterval(() => {
      // We only want to show this on upto tablet size
      if (!uptoTablet) {
        // So if screen size is larger we clear the interval
        !!interval && clearInterval(interval)
      }
      else if (!window.AF_SMART_SCRIPT_RESULT) {
        // We try every 5 seconds 5 times
        circuitBreak++
        // If past 5 times there is no script loaded we clear the interval
        if (circuitBreak > 4) clearInterval(interval)
      }
      else {
        // If the script is loaded and the screen size is upto tablet we show the banner by setting it all up
        // And then we clear the interval
        if (!promoAppDrawer) {
          const oneLinkURL = 'https://luxuryescapes.onelink.me/3WHB'
          const mediaSource = { keys: ['utm_source'], defaultValue: 'af_banner' }
          const campaign = { keys: ['utm_campaign'], defaultValue: 'af_banner_campaign' }
          const googleClickIdKey = 'af_sub1'
          const ad = { defaultValue: 'nopromo' }
          const custom_ss_ui = { paramKey: 'af_ss_ui', defaultValue: 'true' }
          const custom_ss_gtm_ui = { paramKey: 'af_ss_gtm_ui', defaultValue: 'true' }

          window.AF_SMART_SCRIPT_RESULT = window.AF_SMART_SCRIPT.generateOneLinkURL({
            oneLinkURL,
            afParameters: {
              mediaSource,
              campaign,
              googleClickIdKey,
              ad,
              afCustom: [
                custom_ss_ui,
                custom_ss_gtm_ui,
              ],
            },
          })
        }

        setUrl(window.AF_SMART_SCRIPT_RESULT.clickURL)
        setTimeout(() => {
          window.AF_SMART_SCRIPT.fireImpressionsLink()
        }, 1000)
        clearInterval(interval)
      }
    }, 5000)
    return () => {
      // Clear interval before the component unmounts to prevent any memory leaks
      !!interval && clearInterval(interval)
    }
  }, [promoAppDrawer, uptoTablet])

  useEffect(() => {
    if (open && !!url) {
      Analytics.trackClientEvent({
        subject: promoAppDrawer ? 'promo' : 'nopromo',
        action: 'impression',
        category: 'appsflyer_banner',
        type: 'nonInteraction',
      })
    }
  }, [open, url, promoAppDrawer])

  const closeAndSetCookie = useCallback(() => {
    setOpen(false)
    onClose()
    setAppBannerCookie()
  }, [onClose])

  const onGetTheAppClick = useCallback(() => {
    closeAndSetCookie()

    Analytics.trackClientEvent({
      subject: promoAppDrawer ? 'promo' : 'nopromo',
      action: 'clicked',
      category: 'appsflyer_banner',
      type: 'interaction',
      optimizelyEventId: '30361770142',
      optimizelyEventKey: 'click-download-app',
    })
  }, [closeAndSetCookie, promoAppDrawer])

  const copyToClipboard = useCallback(() => {
    if (!code) return
    setCopied(true)
    copyValueToClipboard(code)
    setTimeout(() => {
      setCopied(false)
    }, 3000)
  }, [code])

  return (
    <ModalBase isOpen={!!(open && url)} onClose={closeAndSetCookie}>
      <ModalBody>
        <StyledModalContent>
          <Group direction="vertical" gap={20}>
            <BannerContainer direction="horizontal" horizontalAlign="space-between">
              <BannerContent direction="vertical" gap={16}>
                <Heading variant="heading3">
                  {!promoAppDrawer && <FormattedMessage
                    id="3Yx1FEC95RK0Vbi65ZPw"
                    defaultMessage="Download the app and save even <i>more!</i>"
                    description="Heading prompting user to download the app for additional savings"
                    values={{ i: (chunks) => <i>{chunks}</i> }}
                  />}
                  {promoAppDrawer && discount && code && <FormattedMessage
                    id="NpQt3Qf4Ws3QJTojA2DQ"
                    defaultMessage="Download the app and save an extra <highlight>{discount}</highlight>"
                    description="Heading prompting user to download the app with specific discount amount"
                    values={{
                      discount: <FormatCurrency value={discount} />,
                      highlight: (chunks) => <Heading variant="heading3" colour="highlight-secondary" as="span"><i>{chunks}</i></Heading>,
                    }}
                  />}
                </Heading>
                <Group direction="vertical" gap={4}>
                  <Group direction="horizontal" gap={4} verticalAlign="center">
                    <div>
                      {fillArray(5).map((index) => <SolidStarIcon key={index} size={16} colour="warning" />)}
                    </div>
                    <Caption variant="medium" colour="neutral-two" weight="bold">{intl.formatMessage({
                      id: 'I-KrttvZxOA8CpC9K4pM',
                      defaultMessage: '4.8',
                      description: 'App store rating score displayed in app download drawer',
                    })}
                    </Caption>
                  </Group>
                  <Caption variant="medium" colour="neutral-three">{intl.formatMessage({
                    id: 'MUVHoab4OoM-cWaEP7Hr',
                    defaultMessage: '30.1k+ ratings',
                    description: 'App store rating count displayed in app download drawer',
                  })}
                  </Caption>
                </Group>
              </BannerContent>
              <ResponsiveImage
                id="sa747tnknchju8cwlh1t"
                gravity="auto"
                quality="good"
                width={170}
              />
            </BannerContainer>
            <Content direction="vertical" gap={20}>
              {promoAppDrawer && (
                <Group direction="vertical" gap={8}>
                  <CopyContainer direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between" onClick={copyToClipboard}>
                    <BodyText variant="medium" weight="bold">{code}</BodyText>
                    {copied && <Caption variant="large" weight="bold" colour="highlight-secondary">{intl.formatMessage({
                      id: 'zOjdZM7n_4eDp7BqTjR1',
                      defaultMessage: 'Copied!',
                      description: 'Snackbar text confirming coupon code was copied to clipboard',
                    })}
                    </Caption>}
                    {!copied && <LineCopyIcon />}
                  </CopyContainer>
                  {minSpend && <Caption variant="medium" colour="neutral-three"><FormattedMessage
                    id="fiNWcwW-pR6hUi6YB1w6"
                    defaultMessage="Coupon code only valid on Limited Time LUX Exclusive bookings. Min. spend {minSpendAmount}. T&Cs apply."
                    description="Coupon code terms and conditions with minimum spend amount in app download drawer"
                    values={{
                      minSpendAmount: <FormatCurrency value={minSpend} />,
                    }}
                  />
                  </Caption>}
                </Group>
              )}

              <Group direction="vertical" gap={4}>
                <BodyTextBlock variant="medium" startIcon={<LineBillIcon colour="highlight-secondary" />}>
                  {intl.formatMessage({
                    id: 'QaiNfmhbogw_4CFq7ih4',
                    defaultMessage: 'Unlock exclusive app prices',
                    description: 'Feature text for app download drawer - exclusive pricing',
                  })}
                </BodyTextBlock>
                <BodyTextBlock variant="medium" startIcon={<LineUserCheckIcon colour="highlight-secondary" />}>
                  {intl.formatMessage({
                    id: 'Yn-zAHqdenFe7msUPrbN',
                    defaultMessage: 'Get personalised picks for you',
                    description: 'Feature text for app download drawer - personalised recommendations',
                  })}
                </BodyTextBlock>
                <BodyTextBlock variant="medium" startIcon={<LineThumbsUpIcon colour="highlight-secondary" />}>
                  {intl.formatMessage({
                    id: 'vEOKAFqDoz8e2t1rTxTt',
                    defaultMessage: 'Experience easy booking & management',
                    description: 'Feature bullet text highlighting easy booking and management in the mobile app download drawer',
                  })}
                </BodyTextBlock>
              </Group>
            </Content>
          </Group>
        </StyledModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'm3wWDPDPCmctPp5WRakd',
            defaultMessage: 'Get the app',
            description: 'Primary button text to download the app in the app section',
          }),
          href: url,
          onClick: onGetTheAppClick,
        }}
        secondaryActionProps={{
          children: intl.formatMessage({
            id: '0oLwyJHsoJNgpHVIu1W1',
            defaultMessage: 'Maybe later',
            description: 'Secondary dismiss button text allowing the user to close the mobile app download drawer',
          }),
          onClick: closeAndSetCookie,
        }}
      />
    </ModalBase>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  hasCpcUtmMedium: state.utm.medium === 'cpc',
  hasGoogleClickIdentifier: !!state.router.location.query.gclid,
})

export default connect(mapStateToProps)(ContinueToAppDrawer)
